import React, { useEffect, useState, Suspense } from 'react';
import styled from 'styled-components';
import { fetchUserTimeline } from '../../../services/Http/Api';
import { useTranslation } from 'react-i18next';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageContent from '../../../shared/layout/containers/PageContent';
import LoadingMessage from '../../../shared/layout/feedback/LoadingMessage';
import ErrorMessage from '../../../shared/layout/feedback/ErrorMessage';
import TimelineSkeleton from '../../common/skeletons/TimelineSkeleton';
import StyledCard from '../../../shared/layout/cards/StyledCard';
import { MdTrendingUp, MdGroupAdd, MdBarChart } from 'react-icons/md';

// Dynamically import ReactApexChart
const ReactApexChart = React.lazy(() => import('react-apexcharts'));

const Timeline = () => {
    const { t } = useTranslation();
    const [chartData, setChartData] = useState({ categories: [], series: [] });
    const [stats, setStats] = useState({ total: 0, latest: 0, avg: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const loadTimeline = async () => {
            try {
                setLoading(true);
                const data = await fetchUserTimeline();

                const categories = data.map((item) => item.registration_month);
                const counts = data.map((item) => item.user_count);

                // Calculate cumulative data
                let runningTotal = 0;
                const cumulativeCounts = counts.map(count => {
                    runningTotal += count;
                    return runningTotal;
                });

                // Summary Stats
                const total = runningTotal;
                const latest = counts[counts.length - 1] || 0;
                const avg = counts.length > 0 ? Math.round(total / counts.length) : 0;

                setStats({ total, latest, avg });

                setChartData({
                    categories,
                    series: [
                        {
                            name: t('Users.Timeline.newCustomers', 'Novi kupci'),
                            type: 'bar',
                            data: counts,
                        },
                        {
                            name: t('Users.Timeline.totalCustomers', 'Ukupno kupaca'),
                            type: 'area',
                            data: cumulativeCounts,
                        }
                    ],
                });
            } catch (error) {
                setError(t('Users.Timeline.error'));
                console.error('Error:', error);
            } finally {
                setLoading(false);
            }
        };

        loadTimeline();
    }, [t]);

    if (loading) {
        return <TimelineSkeleton />;
    }

    if (error) {
        return <ErrorMessage>{error}</ErrorMessage>;
    }

    const chartOptions = {
        chart: {
            type: 'line',
            height: 400,
            toolbar: {
                show: false,
            },
            zoom: {
                enabled: false
            }
        },
        colors: ['#821692', '#3fc380'],
        stroke: {
            curve: 'smooth',
            width: [0, 3], // 0 for bar, 3 for line
            dashArray: [0, 5]
        },
        fill: {
            type: ['solid', 'gradient'],
            gradient: {
                shadeIntensity: 1,
                opacityFrom: 0.45,
                opacityTo: 0.05,
                stops: [20, 100]
            }
        },
        xaxis: {
            categories: chartData.categories,
            labels: {
                style: {
                    colors: '#64748b',
                    fontSize: '12px'
                },
                rotate: -45,
            },
            axisBorder: { show: false },
            axisTicks: { show: false }
        },
        yaxis: [
            {
                title: {
                    text: t('Users.Timeline.newCustomers', 'Novi kupci'),
                    style: { color: '#821692', fontWeight: 600 }
                },
                labels: {
                    style: {
                        colors: '#821692',
                        fontSize: '12px'
                    }
                }
            },
            {
                opposite: true,
                title: {
                    text: t('Users.Timeline.totalCustomers', 'Ukupno kupaca'),
                    style: { color: '#3fc380', fontWeight: 600 }
                },
                labels: {
                    style: {
                        colors: '#3fc380',
                        fontSize: '12px'
                    }
                }
            }
        ],
        grid: {
            borderColor: '#f1f5f9',
            strokeDashArray: 4,
            padding: {
                top: 0,
                right: 0,
                bottom: 0,
                left: 10
            }
        },
        tooltip: {
            theme: 'light',
            shared: true,
            intersect: false,
            y: {
                formatter: (val) => `${val} ${t('Users.Timeline.customers', 'kupaca')}`
            }
        },
        legend: {
            position: 'top',
            horizontalAlign: 'right',
            fontSize: '0.8125rem',
            fontFamily: 'inherit',
            fontWeight: 500,
            markers: {
                radius: 12
            }
        },
        plotOptions: {
            bar: {
                borderRadius: 4,
                columnWidth: '60%',
            }
        },
        dataLabels: {
            enabled: false
        }
    };

    return (
        <PageContainer>
            <PageHeader />
            <PageContent>
                <StatsGrid>
                    <StatBox>
                        <IconWrapper color="#821692">
                            <MdTrendingUp size={24} />
                        </IconWrapper>
                        <StatInfo>
                            <StatLabel>{t('Users.Timeline.latestMonth', 'Zadnji mjesec')}</StatLabel>
                            <StatValue>{stats.latest}</StatValue>
                        </StatInfo>
                    </StatBox>
                    <StatBox>
                        <IconWrapper color="#3fc380">
                            <MdGroupAdd size={24} />
                        </IconWrapper>
                        <StatInfo>
                            <StatLabel>{t('Users.Timeline.totalOverall', 'Ukupno kupaca')}</StatLabel>
                            <StatValue>{stats.total}</StatValue>
                        </StatInfo>
                    </StatBox>
                    <StatBox>
                        <IconWrapper color="#1e293b">
                            <MdBarChart size={24} />
                        </IconWrapper>
                        <StatInfo>
                            <StatLabel>{t('Users.Timeline.avgGrowth', 'Prosjek mjesečno')}</StatLabel>
                            <StatValue>{stats.avg}</StatValue>
                        </StatInfo>
                    </StatBox>
                </StatsGrid>

                <StyledCard>
                    <ChartTitle>{t('Users.Timeline.chartTitle', 'Registracije i rast baze kupaca')}</ChartTitle>
                    <Suspense fallback={<LoadingMessage>{t('Users.Timeline.loadingChart')}</LoadingMessage>}>
                        <ReactApexChart
                            options={chartOptions}
                            series={chartData.series}
                            type="line"
                            height={400}
                        />
                    </Suspense>
                </StyledCard>
            </PageContent>
        </PageContainer>
    );
};


export default Timeline;

const StatsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1.25rem;
    margin-bottom: 1.5rem;
`;

const StatBox = styled(StyledCard)`
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 0;
    padding: 1.25rem;
`;

const IconWrapper = styled.div`
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: ${({ color }) => `${color}15`};
    color: ${({ color }) => color};
    display: flex;
    align-items: center;
    justify-content: center;
`;

const StatInfo = styled.div`
    display: flex;
    flex-direction: column;
`;

const StatLabel = styled.span`
    font-size: 0.875rem;
    color: #64748b;
    font-weight: 500;
`;

const StatValue = styled.span`
    font-size: 1.5rem;
    font-weight: 700;
    color: #1e293b;
`;

const ChartTitle = styled.h3`
    font-size: 1.125rem;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 1.5rem;
`;

